// @flow
import React from 'react'
import DatePicker from 'react-datepicker'
import DateService from 'live/services/date'
import CustomDateInput from 'live/components/forms/ui/custom-react-datepicker-input'
import moment from 'moment'
import styled, { CSSObject } from 'styled-components'
import _ from 'lodash'
import LiveLabel from 'live/components/forms/ui/label'

const FallBackLabel = (props: { children: React.Node }) => <label {...props}>{props.children}</label>
const Label = LiveLabel || FallBackLabel

type timestamp = number
export type DateAndTimePickerErrors = {
    noDate?: boolean
    invalidDate?: boolean
    invalidHour?: boolean
}
type Props = {
    title?: string
    timestamp?: timestamp
    maxDate?: timestamp
    onChange: (timestamp: typeof undefined | typeof NaN | timestamp, errors: DateAndTimePickerErrors) => void
    error?: boolean
    required?: boolean
    style?: CSSObject
}

type State = {
    date?: moment
    clockString?: string
}

export default class DateAndTimePicker extends React.Component<Props, State> {
    datePicker = React.createRef()

    constructor(props: Props) {
        super(props)
        this.state = {
            date: props.timestamp ? moment(props.timestamp) : props.timestamp,
            clockString: props.timestamp ? moment(props.timestamp).format('hh:mm:ss A') : ''
        }
    }

    onChangeDate = (mom: moment) => {
        this.setState({ date: mom })
        const { timestamp, errors } = getUnixFromDateAndTime(mom, this.state.clockString)

        this.props.onChange(timestamp, errors)
    }

    onChangeClock = (e: Object) => {
        const { timestamp, errors } = getUnixFromDateAndTime(this.state.date, e.target.value)

        this.setState({
            date: timestamp ? DateService.getMoment(timestamp) : timestamp,
            clockString: e.target.value
        })
        this.props.onChange(timestamp, errors)
    }

    onClockBlur = () => {
        const { timestamp, errors } = getUnixFromDateAndTime(this.state.date, this.state.clockString)

        if (_.isEmpty(errors))
            this.setState({
                clockString: timestamp ? moment(timestamp).format('hh:mm:ss A') : ''
            })
    }

    onDateBlur = () => {
        // workaround: seting inputValue to null on blur clears the user input and shows the actual selected date in case the user inputs an invalid date (which doesnt trigger onChangeDate)
        if (this.datePicker.current) this.datePicker.current.setState({ inputValue: null })
    }

    render() {
        const { date, clockString } = this.state

        return (
            <>
                {this.props.title ? <Label required={this.props.required}>{this.props.title}</Label> : null}
                <InputsContainer>
                    <div>
                        <DatePicker
                            customInput={<CustomDateInput invalid={this.props.error} />}
                            autoComplete="false"
                            selected={date}
                            onChange={this.onChangeDate}
                            dateFormat={DateService.getDateFormat()}
                            placeholderText={DateService.getDateFormat().toLowerCase()}
                            fixedHeight
                            peekNextMonth
                            showYearDropdown
                            showMonthDropdown
                            onBlur={this.onDateBlur}
                            ref={this.datePicker}
                            maxDate={this.props.maxDate}
                        />
                    </div>
                    {/* <div>
                        <MaskedInput
                            className={this.props.error ? 'error' : ''}
                            type="text"
                            value={clockString}
                            onChange={this.onChangeClock}
                            onBlur={this.onClockBlur}
                            mask="11:11:11 AA"
                        />
                    </div> */}
                </InputsContainer>
            </>
        )
    }
}

function getTime(time: string) {
    let numbers = time.slice(0, 8).replace(/_/g, '0')
    let ampm = time.slice(9, 11)

    ampm = ampm === 'AM' || ampm === 'PM' ? ampm : ampm.startsWith('P') ? 'PM' : 'AM'
    let hours = parseInt(numbers.slice(0, 2))

    if (hours > 12 && hours <= 24) {
        hours = hours - 12
        ampm = hours === 12 ? 'AM' : 'PM'
    }
    hours = hours === 0 ? 12 : hours
    numbers = `${hours <= 9 ? '0' : ''}${hours}${numbers.slice(2, 8)}`
    const timeErrors = {}

    if (hours > 12) timeErrors.invalidHour = true

    return { numbers, ampm, timeErrors }
}

function getUnixFromDateAndTime(mom: moment, time?: string) {
    // if everything is undefined, return undefined but no errors
    if (!mom && !time) return { timestamp: undefined, errors: {} }
    const { numbers, ampm, timeErrors } = getTime(time || '__:__:__ AM')

    if (!mom)
        if (mom === undefined)
            // date is undefined but time is defined
            return { timestamp: NaN, errors: { ...timeErrors, noDate: true } }
        // user specified date and time but date is invalid
        else return { timestamp: NaN, errors: { ...timeErrors, invalidDate: true } }

    const m = moment(`${mom.format('YYYYMMDD')}${numbers} ${ampm}`, 'YYYYMMDDhh:mm:ss A', true)

    if (m.isValid()) return { timestamp: m.valueOf(), errors: timeErrors }
    else return { timestamp: mom.valueOf(), errors: timeErrors }
}

const InputsContainer = styled.div`
    display: flex;
    width: 100%;

    & > *:not(:last-child) {
        margin-right: 6px;
    }

    * {
        width: 100%;
    }
`
